// Helpers - three
import SceneWithRaycaster from "./SceneWithRaycaster";
import Cylinder2 from "../Cylinder2";
import Cylinder from "../Cylinder";

// Helpers - common
import VirtualScroll from '../../VirtualScroll'

// libs
import * as THREE from 'three'
import gsap from 'gsap'
import utils from "../../../utils/utils";
import params from "../../../utils/params";

// Constants
const SCALER = 2
const IMAGE_WIDTH = 16 * SCALER
const IMAGE_HEIGHT = 9 * SCALER
const SPACE_BETWEEN_IMAGE = 1.8 * SCALER

const SCROLL_EASE = .2
const SCROLL_SPEED = .002 * SCALER

const TILTING_EASE = .1
const TILTING_LIMITER = .75


const ROTATION_SPEED = -.0009

const THRESHOLD_ROTATION_VISIBLE_BUG = -5.4
const THRESHOLD_ROTATION_INVISIBLE_BUG = 1.74

let CC = ["red", "blue", "green", "pink", "yellow"]

export default class SceneWorkList extends SceneWithRaycaster{

    /**
     * 
     * @param {*} container 
     * @param {*} parameters 
     * @param {Object} data 
     * @param {Array} data.works 
     * @param {Function} data.pushHash
     * @param {String} data.hashOnMounted
     * @param {Boolean} data.canGoBack
     * @param {Function} data.goToPlayer
     */
    constructor(container, parameters, data){
        super(container, {
            ...parameters,
            clearColor: new THREE.Color(1, 1, 1)
        }, data)

        // variables
        this.data = data

        this._textures = []

        this._infinitRotate = true

        // scroll events
        this._currentIndex = 0
        this._isScrolling = false
        this._currentScroll = 0
        this._targetScroll = 0

        // mousemove events
        this._targetMouse = new THREE.Vector2()
        this._currentMouse = new THREE.Vector2()

        this._scrollHeight = 0
        this._indexsHeight = 0
        
        // parameters
        this._canAnimateEvents = false

        // unvariable
        this._origin = new THREE.Vector3()

        this._sceneActive = false

        // bind
        this._onMouseMove = this._onMouseMove.bind(this)
        this._onFreeScroll = this._onFreeScroll.bind(this)
        this.onClickMesh = this.onClickMesh.bind(this)

        window.SceneWorkList = this
    }

    /**
     * Animations
     */

    _slideInNextTexture(nextTexture, prevStep, timeline){

        let prevImage = this._imageGroup.children[prevStep].userData.cylinderTexture.image
        let nextImage = nextTexture.image

        let canvas = this._cylinderInstance.canvas
        let context = this._cylinderInstance.canvasContext

        // context.fillStyle = "red"
        // context.clearRect(0, 0, canvas.width, canvas.height)
        // context.fillRect(0, 0, canvas.width, canvas.height)
        // nextImage = new Image()
        // nextImage.src = canvas.toDataURL()
        // this._cylinder.material.map.needsUpdate = true

        let emptyAnim = {$progress: 0}

        return timeline.to(emptyAnim, {
            duration: params.Animations.durationLong,
            ease: "power4.inOut",

            onUpdate: () => {
                
                let p = emptyAnim.$progress

                let outOffset = p * canvas.width
                let inOffset = nextImage.width * (p - 1)

                // clear prev canvas
                context.clearRect(0, 0, canvas.width, canvas.height)

                // slide out prev texture
                context.drawImage(prevImage, outOffset, 0)


                // slide in next texture
                context.drawImage(nextImage, inOffset, 0)

                this._cylinder.material.map.needsUpdate = true
            }, 

            $progress: 1
        }, 0)


    }

    _animateExit(onComplete = null){

        let prevScroll = this._targetScroll
        let nextScroll = -.05 * window.innerHeight

        let anim = {$progress: 0}

        if(false){
            gsap.to(anim, {
                duration: 1.2,
                ease: params.Animations.easeDecrement,
                onUpdate: () => {
                    this._targetScroll = prevScroll + (nextScroll - prevScroll) * anim.$progress
                },  
                onComplete: () => {
                    this.destroy()
                    onComplete && onComplete()
                },
    
                $progress: 1
            })
        } 
        // immediatly next
        else {
            this.destroy()
            onComplete && onComplete()
        }

    }

    _animateEntrance(){
        
        this.scene.position.y = -IMAGE_HEIGHT * 2

        gsap.to(this.scene.position, {
            duration: 1.2,
            ease: params.Animations.easeDecrement,
            onComplete: () => {
                // can now scroll
                this._canAnimateEvents = true
            },

            y: 0
        })
    }

    triggerForceRotatingCylinder(){

        gsap.to(this._cylinder.rotation, {
            duration: 1,
            ease: "power1.out",
            y: "+=" + (-THRESHOLD_ROTATION_INVISIBLE_BUG),
            onComplete: () => {
                this._infinitRotate = true
            }
        })
    }

    onRender(){
        this.camera.lookAt(this._origin)

        if(!this._sceneActive) return

        if(this._cylinder && this._infinitRotate){
            let nextRotationY = this._cylinder.rotation.y + ROTATION_SPEED
            let modulatedRad = (nextRotationY % (2 * Math.PI))

            if(modulatedRad <= THRESHOLD_ROTATION_VISIBLE_BUG){
                this._infinitRotate = false
                this.triggerForceRotatingCylinder()
            } else {
                this._cylinder.rotation.y = modulatedRad
            }
        }


        if(!this._canAnimateEvents) return;

        /**
         * Tilt cylinder
         */

        let tilting = utils.getTiltingRotation(this._currentMouse, this._targetMouse, TILTING_EASE)

        this._cylinderScaleGroup.rotation.x = -tilting.tiltingX * TILTING_LIMITER
        this._cylinderScaleGroup.rotation.z = -tilting.titlingZ * TILTING_LIMITER

        this._currentMouse.set(tilting.nextX, tilting.nextY)

        /**
         * Scroll
         */

        this._currentScroll += (this._targetScroll - this._currentScroll) * SCROLL_EASE
        this._imageGroup.position.y = this._currentScroll

        let percent = this._currentScroll / this._scrollHeight	

        if(this._imageGroup.children.length !== 1){
            this._drawAllTexture(percent)
        }
    }

    /**
     * Build
     */

    _drawAllTexture(progress){

        // compute translate
        let translate = this._cylinderInstance.canvas.width * (this._imageGroup.children.length - 1) * progress
        
        // Draw all textures
        let allTextTextures = this._imageGroup.children.map(i => i.userData.cylinderTexture.image)
        let texturesCount = allTextTextures.length

        let textureWidth = allTextTextures[0].width
        
        let canvas = this._cylinderInstance.canvas
        let context = this._cylinderInstance.canvasContext

        context.clearRect(0, 0, canvas.width, canvas.height)

        for(let i = 0; i < texturesCount; i++){


            context.drawImage(
                allTextTextures[i],
                i * textureWidth - translate,
                0
            )

            // context.fillStyle = CC[i]

            // context.fillRect(
            //     i * textureWidth - translate,
            //     0,
            //     canvas.width,
            //     canvas.height
            // )


            // let start = i * textureWidth - translate
            // var grd = context.createLinearGradient(start, 0, allTextTextures[i].width, allTextTextures[i].height);
            // grd.addColorStop(0, "red");
            // grd.addColorStop(1, "white");

            // context.fillStyle = grd;
            // context.fillRect(start, 0, allTextTextures[i].width, allTextTextures[i].height);
        }

        this._cylinder.material.map.needsUpdate = true
        this._cylinder.material.needsUpdate = true
    }

    _buildImages(){

        let count = this.data.works.length
        let offset = 0

        // set images group
        this._scrollGroup = new THREE.Group()
        this.scene.add(this._scrollGroup)

        this._imageGroup = new THREE.Group()
        this._scrollGroup.add(this._imageGroup)

        let loader = new THREE.TextureLoader()

        for(let i = 0; i < count; i++){

            // force 16:9
            let imageMeshHeight = IMAGE_HEIGHT

            let geometry = new THREE.PlaneBufferGeometry(IMAGE_WIDTH, imageMeshHeight)
            geometry.translate(0, -imageMeshHeight * .5, 0)

            let material = new THREE.MeshBasicMaterial({
                color: 0x111111,
                side: THREE.DoubleSide,
                map: loader.load(this.data.works[i].src, () => {
                    material.color.setRGB(1, 1, 1)
                    material.needsUpdate = true
                })
            })

            let imageMesh = new THREE.Mesh(geometry, material)
            imageMesh.position.y += - offset
            
            // make it intersectable
            this.intersectableObjects.push(imageMesh)

            // hash
            imageMesh.userData.hash = this.data.works[i].slug

            // player url
            imageMesh.userData.playerSlug = this.data.works[i].slug

            // Cylinder texture
            let cylinder = this._getCylinderFrom(i)
            imageMesh.userData.cylinderTexture = cylinder.mesh.material.map
            cylinder.destroy()
            cylinder = null

            // add to group
            this._imageGroup.add(imageMesh)

            // increment offset
            offset += imageMeshHeight + SPACE_BETWEEN_IMAGE

        }


        this._scrollGroup.position.y = this._imageGroup.children[0].geometry.parameters.height * .5

    }

    _getCylinderFrom(workIndex){
        let primary = this.data.works[workIndex].primary.toUpperCase()
        let secondary = this.data.works[workIndex].secondary.toUpperCase()

        let cylinder = new Cylinder2(
            [
                {text: primary, fontSize: 35, fontWeight: 700, fontFamily: "GrtskMegaBold"},
                {text: secondary, fontSize: 35, fontWeight: 200, fontFamily: "GrtskMegaExtralight"},
            ], {
                // repeat: 17,
                ratio: 70,
                cylinderRadius: (IMAGE_WIDTH / 3) * 1.6,
                // spaceBetween: 25,
                radialSegments: 128 * 4,
                isEllipse: true,
                // nearestPow2: false,
                anisotropy: this.renderer.capabilities.getMaxAnisotropy(),
            }
        )

        // make it repeatable
        cylinder.mesh.material.map.wrapS = THREE.RepeatWrapping

        return cylinder

    }

    _buildCylinder(){

        // group that rotate
        this._cylinderRotateGroup = new THREE.Group()
        this.scene.add(this._cylinderRotateGroup)
        // this._cylinderRotateGroup.rotation.z = Math.PI * .075
        
        // group that scale
        this._cylinderScaleGroup = new THREE.Group()
        this._cylinderRotateGroup.add(this._cylinderScaleGroup)

        // create cylinder from first index of works
        this._cylinderInstance = this._getCylinderFrom(this._currentIndex)
        this._cylinder = this._cylinderInstance.mesh
        this._cylinderScaleGroup.add(this._cylinder)

        this._drawAllTexture(0)

        // make it ellipse
        this._cylinderScaleGroup.scale.x = 1.5
        this._cylinderScaleGroup.scale.y = 1.5

        this._cylinderScaleGroup.scale.multiplyScalar(.7)

        // start rotation at 
        this._cylinder.rotation.y = THRESHOLD_ROTATION_VISIBLE_BUG + THRESHOLD_ROTATION_INVISIBLE_BUG

        // this._cylinder.geometry.attributes.position.array = this._cylinder.geometry.attributes.position.array.map((p, index) => {
        //     if(index % 3 === 2){
        //         return p * 1.5
        //     } else {
        //         return p
        //     }
        // })
    }

    _restoreHashPosition(){

        let hashOnMounted = this.data.hashOnMounted
        let matchedIndex = this._imageGroup.children.findIndex(i => i.userData.hash === hashOnMounted)

        if(matchedIndex !== -1 && this._imageGroup.children.length > 1){

            // set current index
            this._currentIndex = matchedIndex

            // restore scroll quantity
            let percent = matchedIndex / (this._imageGroup.children.length - 1)

            this._targetScroll = percent * this._scrollHeight
            this._currentScroll = percent * this._scrollHeight

            // set current position
            this._imageGroup.position.y = this._getImagePosition(matchedIndex)
        }

    }

    initScene(){
        super.initScene()
        this._buildScene()
    }

    _buildScene(){


        CC = this.data.works.map((m, mi) => {
            let x = (mi / this.data.works.length)
            return `rgb(${Math.random() * 250}, ${Math.random() * 250}, ${Math.random() * 250})`
        })

        // set camera
        this.camera.position.z = IMAGE_WIDTH * .8

        // set light
        this.scene.add(new THREE.AmbientLight("#fff", .1))

        this._buildImages()

        // compute params for scroll
        this.computeScrollHeight()

        // it set the currentIndex
        this._restoreHashPosition()

        this._buildCylinder()


        // draw correct texture position
        let percent = this._currentScroll / this._scrollHeight	

        if(this._imageGroup.children.length !== 1){
            this._drawAllTexture(percent)
        }

        this._sceneActive = true
    }

    /**
     * Helpers
     */

    _getImagePosition(index){
        let image = this._imageGroup.children[index]
        if(image){
            return -image.userData.centerPos
        } else {
            return 0
        }
    }

    computeScrollHeight(){

        // center pos
        let length = this._imageGroup.children.length
        for(let i = 0; i < length; i++){
            let img = this._imageGroup.children[i]
            img.userData.centerPos = img.position.y
        }

        // scroll height

        // get images heights
        let heights = this._imageGroup.children.map(m => m.geometry.parameters.height)

        // sum of hegiht
        let sumHeights = heights.reduce((acc, cur) => acc + cur, 0)
        sumHeights += -(heights[heights.length - 1] + heights[0]) * .5

        // including spaces
        let heightsWithSpaces = sumHeights + (this._imageGroup.children.length - 1) * SPACE_BETWEEN_IMAGE

        let heightForSteps = heightsWithSpaces - (heights[heights.length - 1] + SPACE_BETWEEN_IMAGE) * .5

        /**
        * Set scroll height
        */
        this._scrollHeight = heightsWithSpaces

        /**
        * Set steps height
        */
        this._stepsHeight = heightForSteps
    }

    /**
     * Events
     */

    onClickMesh(){

        
        let mesh = this.hoveredMesh

        if(mesh){
            let slug = mesh.userData.playerSlug
            this.data.goToPlayer(slug)
        }

    }

    onMouseEnterMesh(intersect){
        super.onMouseEnterMesh(intersect)

        let mesh = intersect.object

        // pointer clickable
        document.body.style.cursor = "pointer"        
    }

    onMouseLeaveMesh(){

        let mesh = this.hoveredMesh

        // pointer clickable
        document.body.style.cursor = ""

        super.onMouseLeaveMesh()
    }
    
    getMousePosition(event){
        return [
            (event.clientX * 2 - window.innerWidth) / window.innerWidth,
            -(event.clientY * 2 - window.innerHeight) / window.innerHeight
        ]
    }

    _onMouseMove(event){

        let currentMouse = utils.getMousePosition(event)

        this._targetMouse.set(
            currentMouse[0],
            currentMouse[1]
        )
    }

    _onStepChange(step){	

        let prevStep = this._currentIndex	

        // set next stepx	
        this._currentIndex = step	

        let nextImage = this._imageGroup.children[step]

        // push next hash
        let nextHash = nextImage.userData.hash
        this.data.pushHash(nextHash)
    }

    _onFreeScroll(event){
        
        if(!this._canAnimateEvents) return;	

        // increment target scroll	
        this._targetScroll += -event.deltaY * SCROLL_SPEED	
        
        // clamp	 
        this._targetScroll = utils.limit(this._targetScroll, 0, this._scrollHeight)	


        // Go back home
        if(this.data.goBack && this._targetScroll === 0 && event.deltaY > 10){
            // prevent other scroll
            this._canAnimateEvents = true
            this.removeEvents()

            this.data.goBack()
            return
        }

        // get percent	
        let percent = (this._targetScroll / this._scrollHeight) || 0 // prevent NaN when there is 1 items

        // get current step	
        let step = Math.floor(percent * 2 * (this._imageGroup.children.length - 1))	

        // incremented on impair number	
        step = Math.floor((step + 1) / 2)	

        // we reach a new step	
        if(step !== this._currentIndex){	
            this._onStepChange(step)	
        }  
    }

    attachEvents(){
        super.attachEvents()

        VirtualScroll.on(this._onFreeScroll)	
        this.container.addEventListener("click", this.onClickMesh)
        window.addEventListener("mousemove", this._onMouseMove)

    }

    removeEvents(){
        super.removeEvents()

        VirtualScroll.off(this._onFreeScroll)	
        this.container.removeEventListener("click", this.onClickMesh)
        window.removeEventListener("mousemove", this._onMouseMove)
    }

    /**
     * Core
     */

    start(){
        this._animateEntrance()
        super.start()
    }
}